import DriverAddParams, {
  DriverAddParamsAddress,
  DriverAddParamsDriver,
  DriverAddParamsTaxiDriverAssociation,
  DriverAddParamsTaxiDriverAssociationForB2B,
  DriverAddParamsTaxiDriverAssociationForEmployment,
} from "../../../../../common/services/driver/add/driver-add-params";
import AddressType from "../../../../../common/types/address-type";
import UserFormOfEmployment from "../../../../../common/types/user-form-of-employment";
import dateService from "../../../../../common/utils/date/date.service";
import SignUpDriverAddressType from "./types/sign-up-driver-address-type";
import SignUpDriverFormAccountData from "./types/sign-up-driver-form-account-data";
import SignUpDriverFormCompanyData from "./types/sign-up-driver-form-company-data";
import SignUpDriverFormUserAddress from "./types/sign-up-driver-form-user-address";
import SignUpDriverFormUserData from "./types/sign-up-driver-form-user-data";
import SignUpDriverFormVehicleData from "./types/sign-up-driver-form-vehicle-data";

const createAddressType = (type: SignUpDriverAddressType): AddressType => {
  switch (type) {
    case SignUpDriverAddressType.MAILING:
      return AddressType.MAILING;
    case SignUpDriverAddressType.STARTING:
      return AddressType.STARTING;
  }
};

const createSignUpRequestAddress = (
  userDataAddress: SignUpDriverFormUserAddress,
): DriverAddParamsAddress => {
  return {
    apartment: userDataAddress.apartmentNumber.trim(),
    country: userDataAddress.country.trim(),
    description: userDataAddress.description.trim(),
    houseNo: userDataAddress.houseNumber.trim(),
    lat: userDataAddress.latitude,
    lon: userDataAddress.longitude,
    street: userDataAddress.street.trim(),
    town: userDataAddress.town.trim(),
    type: createAddressType(userDataAddress.type),
    zipCode: userDataAddress.zipCode.trim(),
  };
};

const createSignUpRequestAddresses = (
  userDataAddresses: SignUpDriverFormUserAddress[],
): DriverAddParamsAddress[] => {
  return userDataAddresses.map(createSignUpRequestAddress);
};

const createSignUpRequestTaxiDriverAssociationForEmployment = (
  userData: SignUpDriverFormUserData,
  taxiCorporationId: string,
): DriverAddParamsTaxiDriverAssociationForEmployment => {
  return {
    contractType: userData.formOfEmployment!.value,
    taxiCorporationId: taxiCorporationId,
    displayName: `${userData.firstName.trim()} ${userData.lastName.trim()}`,
    fleetPartnerId: null,
  };
};

const createSignUpRequestTaxiDriverAssociationForB2B = (
  userData: SignUpDriverFormUserData,
  companyData: SignUpDriverFormCompanyData,
  taxiCorporationId: string,
): DriverAddParamsTaxiDriverAssociationForB2B => {
  return {
    apartment: companyData.address.apartmentNumber.trim(),
    companyName: companyData.name,
    contractType: userData.formOfEmployment!.value,
    country: companyData.address.country.trim(),
    description: companyData.address.description.trim(),
    houseNo: companyData.address.houseNumber.trim(),
    regon: companyData.companyId.trim() || null,
    taxId: companyData.taxNumber.trim(),
    zipCode: companyData.address.zipCode.trim(),
    town: companyData.address.town.trim(),
    krsNo: companyData.nationalCourtRegisterNumber.trim(),
    street: companyData.address.street.trim(),
    taxiCorporationId: taxiCorporationId,
    displayName: `${userData.firstName.trim()} ${userData.lastName.trim()}`,
    fleetPartnerId: null,
  };
};

const createSignUpRequestTaxiDriverAssociation = (
  userData: SignUpDriverFormUserData,
  companyData: SignUpDriverFormCompanyData,
  taxiCorporationId: string,
): DriverAddParamsTaxiDriverAssociation => {
  if (userData.formOfEmployment?.value === UserFormOfEmployment.B2B) {
    return createSignUpRequestTaxiDriverAssociationForB2B(
      userData,
      companyData,
      taxiCorporationId,
    );
  }

  return createSignUpRequestTaxiDriverAssociationForEmployment(
    userData,
    taxiCorporationId,
  );
};

const createDriver = (
  userData: SignUpDriverFormUserData,
  vehicleData: SignUpDriverFormVehicleData,
  companyData: SignUpDriverFormCompanyData,
  taxiCorporationId: string,
): DriverAddParamsDriver => {
  return {
    birthDate: dateService.format(userData.birthDate!, "yyyy-mm-dd"),
    birthPlace: userData.birthPlace,
    citizenship: userData.citizenship!.value,
    experience: userData.yearsOfExperience!,
    idNumber: userData.personalIdNumber,
    languages: userData.languages.map((lang) => lang.value),
    carMake: vehicleData.make,
    carModel: vehicleData.model,
    carOwnership: vehicleData.ownership!.value,
    carProdYear: vehicleData.productionYear!,
    carRegNo: vehicleData.registrationNumber,
    carSeatsNo: vehicleData.numberOfSeats!,
    addresses: createSignUpRequestAddresses(userData.addresses),
    taxiDriverAssociations: [
      createSignUpRequestTaxiDriverAssociation(
        userData,
        companyData,
        taxiCorporationId,
      ),
    ],
  };
};

const createSignUpRequest = ({
  accountData,
  userData,
  companyData,
  vehicleData,
  taxiCorporationId,
}: {
  accountData: SignUpDriverFormAccountData;
  userData: SignUpDriverFormUserData;
  companyData: SignUpDriverFormCompanyData;
  vehicleData: SignUpDriverFormVehicleData;
  taxiCorporationId: string;
}): DriverAddParams => {
  return {
    email: accountData.email.trim(),
    username: accountData.username.trim(),
    password: accountData.password,
    firstName: userData.firstName.trim(),
    lastName: userData.lastName.trim(),
    mobilePrimaryPrefix: userData.mobile.dialingCode!,
    mobilePrimary: userData.mobile.number!,
    mobileSecondaryPrefix: userData.alternativeMobile.dialingCode,
    mobileSecondary: userData.alternativeMobile.number,
    driver: createDriver(userData, vehicleData, companyData, taxiCorporationId),
  };
};

const signUpDriverFormFactory = {
  createSignUpRequest,
};

export default signUpDriverFormFactory;
